import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import ErrorPopupComponent from "../error-popup/error-popup.component.vue";
import { complaintApi } from "@/providers/apis/complaint";
import { User } from "@/models/common-interface/common.interface";
import { utils } from "@/utils/utils";
import {
  SortingOrderKey,
  TableSortEvent,
} from "@/models/constants/filters.interface";

@Component({
  name: "reviewer-complaint-page",
  components: {
    AppLoaderComponent,
    ErrorPopupComponent,
  },
})
export default class ReviewerComplaintPage extends Vue {
  public complaintData: any[] = [];

  public limit = 10;
  public offset = 0;
  public currentPage = 1;
  public page = 1;
  public totalItem = 0;

  public isLoading = true;
  public isDataEmpty = false;
  public errorMessage = "";

  /** The API sorts by column name and whitelists what it accepts. */
  public sort = "submitted_at";
  public sortBy: string = SortingOrderKey.DESC;
  /**
   * b-table hands back the field key, and the API sorts by column name, so the
   * two are paired here. A key absent from this map is not sortable.
   */
  private sortColumns: Record<string, string> = {
    submittedAt: "submitted_at",
    fullName: "full_name",
    email: "email",
    program: "program",
    status: "status",
  };

  public filterDetails: any = {};
  public toogleStatus = false;
  public selected: any = [];
  public applyStatusFilter: any = [];
  public statusFilterData = "";

  public toogleProgram = false;
  public selectedPrograms: any = [];
  public applyProgramFilter: any = [];
  public programFilterData = "";

  public userDetails!: User;

  /**
   * Deliberately no id column: the acknowledgment code identifies a complaint
   * to the person who filed it, and staff work from who/when instead.
   */
  public tableFields = [
    {
      key: "submittedAt",
      label: "Submission Date",
      sortable: true,
      thClass: "application_date",
    },
    { key: "fullName", label: "Filed By", sortable: true },
    {
      key: "email",
      label: "Email Address",
      sortable: true,
      thClass: "email",
      tdClass: "email",
    },
    { key: "program", label: "Program", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      thClass: "application_status",
      tdClass: "progress-status",
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      thClass: "application_action pointer-events",
      tdClass: "actions",
    },
  ];

  public options = [
    { text: "Received", value: "RECEIVED" },
    { text: "Reviewing", value: "REVIEWING" },
    { text: "Closed", value: "CLOSED" },
  ];

  /** One queue is shared across the three portals, so all three are offered. */
  public programOptions = [
    { text: "CPRS", value: "CPRS" },
    { text: "CFSS", value: "CFSS" },
    { text: "CYAPSS", value: "CYAPSS" },
  ];

  public created() {
    this.userDetails = utils.getUserDetails();
    this.getComplaintList();
  }

  public updated() {
    window.document.onclick = (event) => {
      // Both dropdowns carry .filter-status, so each is matched by a selector
      // that can only hit one of them rather than by position.
      if (this.toogleProgram) {
        const el = document.querySelector(".filter-program");
        if (el && !el.contains(event.target as Node)) {
          this.toogleProgram = false;
        }
      }
      if (this.toogleStatus) {
        const el = document.querySelector(
          ".filter-status:not(.filter-program)"
        );
        if (el && !el.contains(event.target as Node)) {
          this.toogleStatus = false;
        }
      }
    };
  }

  public getComplaintList() {
    this.isLoading = true;
    const filters: any = {
      limit: this.limit,
      offset: this.offset,
      sort: this.sort,
      sortBy: this.sortBy,
      status: this.statusFilterData,
      program: this.programFilterData,
      fullName: this.filterDetails.fullName,
      email: this.filterDetails.email
        ? encodeURIComponent(this.filterDetails.email)
        : null,
    };
    complaintApi
      .getComplaintList(filters)
      .then((data: any) => {
        this.isLoading = false;
        this.complaintData = data.list || [];
        this.totalItem = data.totalCount || 0;
        this.isDataEmpty = !this.complaintData.length;
        this.complaintData.forEach((row: any) => {
          row.submittedAt = row.submittedAt
            ? moment(row.submittedAt).format("MM-DD-YYYY")
            : "--";
        });
      })
      .catch((error: any) => {
        this.isDataEmpty = true;
        this.isLoading = false;
        this.errorMessage = utils.errorMessage(error);
      });
  }

  public onApplyFilter() {
    this.offset = 0;
    this.currentPage = 1;
    this.toogleStatus = !this.toogleStatus;
    this.applyStatusFilter = this.selected;
    this.statusFilterData = this.applyStatusFilter.join(",");
    this.getComplaintList();
  }

  public onClearFilter() {
    this.offset = 0;
    this.currentPage = 1;
    this.toogleStatus = !this.toogleStatus;
    this.selected = [];
    this.applyStatusFilter = [];
    this.statusFilterData = "";
    this.getComplaintList();
  }

  public onApplyProgramFilter() {
    this.offset = 0;
    this.currentPage = 1;
    this.toogleProgram = false;
    this.applyProgramFilter = this.selectedPrograms;
    this.programFilterData = this.applyProgramFilter.join(",");
    this.getComplaintList();
  }

  public onClearProgramFilter() {
    this.offset = 0;
    this.currentPage = 1;
    this.toogleProgram = false;
    this.selectedPrograms = [];
    this.applyProgramFilter = [];
    this.programFilterData = "";
    this.getComplaintList();
  }

  public onClearProgramChip(index: any) {
    this.applyProgramFilter.splice(index, 1);
    this.selectedPrograms = this.applyProgramFilter;
    this.programFilterData = this.applyProgramFilter.join(",");
    this.offset = 0;
    this.currentPage = 1;
    this.getComplaintList();
  }

  public onClearStatusFilter(index: any) {
    this.applyStatusFilter.splice(index, 1);
    this.selected = this.applyStatusFilter;
    this.statusFilterData = this.applyStatusFilter.join(",");
    this.offset = 0;
    this.currentPage = 1;
    this.getComplaintList();
  }

  public onClearAllFilterStatus() {
    this.offset = 0;
    this.currentPage = 1;
    this.selected = [];
    this.applyStatusFilter = [];
    this.statusFilterData = "";
    this.selectedPrograms = [];
    this.applyProgramFilter = [];
    this.programFilterData = "";
    this.filterDetails.fullName = "";
    this.filterDetails.email = "";
    this.sort = "submitted_at";
    this.sortBy = SortingOrderKey.DESC;
    this.getComplaintList();
  }

  public loadContent(page: number) {
    this.page = page;
    this.offset = (this.page - 1) * this.limit;
    this.getComplaintList();
  }

  public sortChanged(event: TableSortEvent) {
    if (event) {
      this.offset = 0;
      this.currentPage = 1;
    }
    const column = event.sortBy ? this.sortColumns[event.sortBy] : undefined;
    if (column) {
      this.sort = column;
      this.sortBy = event.sortDesc ? SortingOrderKey.DESC : SortingOrderKey.ASC;
    }
    this.getComplaintList();
  }

  /** Blank until a status is set, which only happens once assigned. */
  public statusLabel(status: string): string {
    if (!status) {
      return "";
    }
    const match = this.statusOption(status);
    return match ? match.text : status;
  }

  public statusClass(status: string): string {
    const value = (status || "").toUpperCase();
    if (value === "REVIEWING") return "reviewing";
    if (value === "CLOSED") return "closed";
    return "received";
  }

  private statusOption(status: string) {
    return this.options.find((option) => option.value === status);
  }

  /** Assigning, status changes and comments all live on the detail page. */
  public previewComplaint(row: any) {
    this.$router.push({ path: `complaints/${row.item.id}` });
  }

  // Event name matches ErrorPopupComponent's existing (misspelled) emit.
  public closdedErrorPopup(event: boolean) {
    if (event) {
      this.errorMessage = "";
    }
  }
}
